// src/features/explore/filterExplore.ts — pure filtering for the explore views.
import type { ExploreItem } from '@/core/models/explore';
import type { SuggestedProfile } from '@/core/models/account';
import type { Community } from '@/core/models/community';
import type { FeedTab, ExploreSortId } from './meta';

export interface ExploreFilter {
  topic: string;
  search: string;
  tab: FeedTab;
  sort: ExploreSortId;
}

function matches(haystack: string, search: string): boolean {
  const q = search.trim().toLowerCase();
  if (q === '') return true;
  return haystack.toLowerCase().includes(q);
}

/** Pure: filter the explore feed by topic + search + tab, then sort. Never mutates input. */
export function filterExplore(items: ExploreItem[], f: ExploreFilter): ExploreItem[] {
  const filtered = items.filter((item) => {
    if (f.topic !== 'all' && item.topic !== f.topic) return false;
    if (!matches(`${item.title} ${item.author} ${item.handle} ${item.topic}`, f.search)) return false;
    // 'for-you' and 'latest' apply no tab filter — their ordering comes from `sort`.
    if (f.tab === 'trending' && !item.trending) return false;
    if (f.tab === 'popular' && !item.popular) return false;
    return true;
  });

  const sorted = [...filtered];
  if (f.sort === 'cheers') sorted.sort((a, b) => b.cheers - a.cheers);
  else sorted.sort((a, b) => b.ts - a.ts);
  return sorted;
}

/** Pure: filter suggested profiles by name/handle substring. Empty search passes all. */
export function filterPeople(list: SuggestedProfile[], search: string): SuggestedProfile[] {
  return list.filter((p) => matches(`${p.name} ${p.handle}`, search));
}

/** Pure: filter communities by name/description substring. Empty search passes all. */
export function filterCommunities(list: Community[], search: string): Community[] {
  return list.filter((c) => matches(`${c.name} ${c.description}`, search));
}
